import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getProducts, getCategories } from '../utils/api';
import { useSEO } from '../utils/useSEO';
import ProductCard from '../components/common/ProductCard';
import { FiX, FiChevronDown, FiChevronUp, FiSliders } from 'react-icons/fi';
import styles from './ProductsPage.module.css';

const SORT_OPTIONS = [
  { value: 'newest',     label: '🆕 Newest First' },
  { value: 'popular',    label: '🔥 Most Popular' },
  { value: 'rating',     label: '⭐ Top Rated' },
  { value: 'price_asc',  label: '💰 Price: Low → High' },
  { value: 'price_desc', label: '💰 Price: High → Low' },
];

function FilterPanel({ categories, params, setParam, clearFilters, hasFilters }) {
  const [priceOpen, setPriceOpen] = useState(true);
  const [catOpen, setCatOpen] = useState(true);

  return (
    <div className={styles.filterPanel}>
      {hasFilters && (
        <div className={styles.filterHeader}>
          <span className={styles.filterCount}>Filters Applied</span>
          <button onClick={clearFilters} className={styles.clearAllBtn}><FiX size={12}/> Clear All</button>
        </div>
      )}

      {/* Category filter */}
      <div className={styles.filterGroup}>
        <button className={styles.filterGroupTitle} onClick={() => setCatOpen(o=>!o)}>
          Categories {catOpen ? <FiChevronUp size={14}/> : <FiChevronDown size={14}/>}
        </button>
        {catOpen && (
          <div className={styles.filterGroupBody}>
            <button className={`${styles.catFilterBtn} ${!params.get('category') ? styles.catActive : ''}`} onClick={() => setParam('category', '')}>
              All Categories
            </button>
            {categories.map(c => (
              <button
                key={c._id}
                className={`${styles.catFilterBtn} ${params.get('category') === c._id ? styles.catActive : ''}`}
                onClick={() => setParam('category', c._id)}
              >
                {c.icon} {c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Price filter */}
      <div className={styles.filterGroup}>
        <button className={styles.filterGroupTitle} onClick={() => setPriceOpen(o=>!o)}>
          Price Range (৳) {priceOpen ? <FiChevronUp size={14}/> : <FiChevronDown size={14}/>}
        </button>
        {priceOpen && (
          <div className={styles.filterGroupBody}>
            {[
              { label: 'Under ৳500',         min:'', max:'500' },
              { label: '৳500 – ৳2,000',       min:'500', max:'2000' },
              { label: '৳2,000 – ৳10,000',    min:'2000', max:'10000' },
              { label: '৳10,000 – ৳50,000',   min:'10000', max:'50000' },
              { label: 'Over ৳50,000',         min:'50000', max:'' },
            ].map(r => {
              const active = params.get('minPrice') === r.min && params.get('maxPrice') === r.max;
              return (
                <button key={r.label}
                  className={`${styles.catFilterBtn} ${active ? styles.catActive : ''}`}
                  onClick={() => { setParam('minPrice', r.min); setParam('maxPrice', r.max); }}
                >
                  {r.label}
                </button>
              );
            })}
            <div className={styles.customPrice}>
              <input type="number" className="form-input" placeholder="Min" value={params.get('minPrice') || ''} onChange={e => setParam('minPrice', e.target.value)} style={{ fontSize:'0.82rem', padding:'7px 10px' }}/>
              <span>–</span>
              <input type="number" className="form-input" placeholder="Max" value={params.get('maxPrice') || ''} onChange={e => setParam('maxPrice', e.target.value)} style={{ fontSize:'0.82rem', padding:'7px 10px' }}/>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [total, setTotal]         = useState(0);
  const [pages, setPages]         = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const page    = parseInt(searchParams.get('page') || '1');
  const search  = searchParams.get('search') || '';
  const sort    = searchParams.get('sort')   || 'newest';
  const catId   = searchParams.get('category') || '';
  const featured = searchParams.get('featured') || '';

  const hasFilters = catId || search || featured || searchParams.get('minPrice') || searchParams.get('maxPrice');

  useSEO({
    title: search ? `Search: ${search}` : catId ? 'Products' : 'All Products',
    description: `Browse ${total} products on BDShop. Fast delivery, bKash & Cash on Delivery.`,
    keywords: `shop online bangladesh, ${search || 'electronics fashion food'}, buy online`,
  });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const p = { page, limit: 16, sort };
      if (search)   p.search = search;
      if (catId)    p.category = catId;
      if (featured) p.featured = featured;
      if (searchParams.get('minPrice')) p.minPrice = searchParams.get('minPrice');
      if (searchParams.get('maxPrice')) p.maxPrice = searchParams.get('maxPrice');
      const { data } = await getProducts(p);
      setProducts(data.products);
      setTotal(data.total);
      setPages(data.pages);
    } finally { setLoading(false); }
  }, [page, search, sort, catId, featured, searchParams]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { getCategories().then(r => setCategories(r.data)); }, []);

  const setParam = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  };

  const clearFilters = () => {
    setSearchParams({});
    setDrawerOpen(false);
  };

  // Lock body when drawer open on mobile
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const selectedCatName = categories.find(c => c._id === catId)?.name;

  return (
    <div className={styles.page}>
      {/* Page heading */}
      <div className={styles.pageHead}>
        <div className="container">
          <div className={styles.pageHeadInner}>
            <div>
              <h1 className={styles.pageTitle}>
                {featured ? '🔥 Featured Products' : search ? `Results for "${search}"` : selectedCatName ? `${selectedCatName}` : 'All Products'}
              </h1>
              <p className={styles.pageCount}>{loading ? '...' : `${total.toLocaleString()} products found`}</p>
            </div>
            <div className={styles.sortWrap}>
              <label htmlFor="sort-select" className={styles.sortLabel}>Sort by:</label>
              <select id="sort-select" className="form-input form-select" style={{ width:'auto', fontSize:'0.875rem' }} value={sort} onChange={e => setParam('sort', e.target.value)}>
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Mobile filter button */}
        <div className={styles.mobileFilterBar}>
          <button className={`btn btn-outline btn-sm ${styles.filterToggleBtn}`} onClick={() => setDrawerOpen(true)}>
            <FiSliders size={14} /> Filters {hasFilters && <span className={styles.filterDot} />}
          </button>
          {hasFilters && (
            <div className={styles.activeFilters}>
              {catId && <span className={styles.filterChip}>{selectedCatName} <button onClick={() => setParam('category','')}><FiX size={10}/></button></span>}
              {search && <span className={styles.filterChip}>"{search}" <button onClick={() => setParam('search','')}><FiX size={10}/></button></span>}
              {featured && <span className={styles.filterChip}>Featured <button onClick={() => setParam('featured','')}><FiX size={10}/></button></span>}
            </div>
          )}
        </div>

        <div className={styles.layout}>
          {/* Sidebar - desktop */}
          <aside className={styles.sidebar} aria-label="Product filters">
            <FilterPanel categories={categories} params={searchParams} setParam={setParam} clearFilters={clearFilters} hasFilters={!!hasFilters}/>
          </aside>

          {/* Mobile drawer */}
          {drawerOpen && <div className={styles.drawerOverlay} onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
          <div className={`${styles.filterDrawer} ${drawerOpen ? styles.drawerOpen : ''}`} role="dialog" aria-label="Filters">
            <div className={styles.drawerHeader}>
              <span>🔧 Filters</span>
              <button onClick={() => setDrawerOpen(false)}><FiX size={20}/></button>
            </div>
            <div style={{ overflowY:'auto', flex:1 }}>
              <FilterPanel categories={categories} params={searchParams} setParam={(k,v) => { setParam(k,v); setDrawerOpen(false); }} clearFilters={clearFilters} hasFilters={!!hasFilters}/>
            </div>
          </div>

          {/* Products */}
          <main className={styles.main} id="product-results" aria-live="polite" aria-busy={loading}>
            {loading ? (
              <div className="products-grid">
                {[...Array(12)].map((_,i) => (
                  <div key={i} style={{ background:'#fff', borderRadius:12, overflow:'hidden', border:'1px solid var(--border)' }}>
                    <div className="skeleton" style={{ aspectRatio:1 }} />
                    <div style={{ padding:12, display:'flex', flexDirection:'column', gap:8 }}>
                      <div className="skeleton" style={{ height:12, width:'55%' }} />
                      <div className="skeleton" style={{ height:14, width:'85%' }} />
                      <div className="skeleton" style={{ height:14, width:'70%' }} />
                      <div className="skeleton" style={{ height:32, marginTop:4 }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className={styles.empty}>
                <span>🔍</span>
                <h3>No products found</h3>
                <p>Try different filters or search terms</p>
                <button className="btn btn-primary" onClick={clearFilters}>Clear All Filters</button>
              </div>
            ) : (
              <>
                <div className="products-grid">
                  {products.map(p => <ProductCard key={p._id} product={p} />)}
                </div>

                {/* Pagination */}
                {pages > 1 && (
                  <nav className="pagination" aria-label="Page navigation">
                    <button className="page-btn" disabled={page===1} onClick={() => setParam('page', String(page-1))} aria-label="Previous page">‹</button>
                    {Array.from({ length: pages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === pages || Math.abs(p - page) <= 2)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && arr[idx-1] !== p-1) acc.push('...');
                        acc.push(p); return acc;
                      }, [])
                      .map((p, i) => typeof p === 'string'
                        ? <span key={`e${i}`} style={{ padding:'0 4px', color:'var(--text-muted)' }}>…</span>
                        : <button key={p} className={`page-btn ${p===page?'active':''}`} onClick={() => setParam('page', String(p))} aria-label={`Page ${p}`} aria-current={p===page?'page':undefined}>{p}</button>
                      )
                    }
                    <button className="page-btn" disabled={page===pages} onClick={() => setParam('page', String(page+1))} aria-label="Next page">›</button>
                  </nav>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
